import {accumulateStreamEvent, type ConversationMessage, type MessageContentFunctionCall, type MessageContentFunctionResult} from "@/services/wingman";

const isDeniedToolResult = (result: unknown): boolean =>
  typeof result === "object" && result !== null && "denied" in result && result.denied === true;

const withApprovalDecision = (
  contents: ConversationMessage["contents"],
  callId: string,
  approved: boolean
): ConversationMessage["contents"] =>
  contents.map((content) =>
    content.type === "function_call" && content.callId === callId && content.approval
      ? {...content, approval: {...content.approval, approved}}
      : content
  );

/** Fold a continuation call/result into the original approval proposal message. */
export const applyApprovalContinuation = (
  item: ConversationMessage,
  approvalProposalId: string,
  data: MessageContentFunctionCall | MessageContentFunctionResult
): ConversationMessage => {
  if (item.id !== approvalProposalId) return item;
  const updated = accumulateStreamEvent(item, data);
  if (data.type !== "function_result") return updated;
  return {
    ...updated,
    contents: withApprovalDecision(updated.contents, data.callId, !isDeniedToolResult(data.result)),
  };
};

/** Restore persisted approval state without dropping results already received by this client. */
export function mergeApprovalRecovery(current: ConversationMessage[], history: ConversationMessage[] | undefined, proposalId: string): ConversationMessage[] {
  const proposalIndex = current.findIndex(message => message.id === proposalId);
  if (proposalIndex < 0) return current;
  // Without authoritative history, discard only the uncommitted continuation.
  // Acknowledged calls stay locked until the server confirms their state.
  if (!history?.some(message => message.id === proposalId)) return current.slice(0, proposalIndex + 1);
  const persistedResultCallIds = new Set(history.flatMap(message => message.contents.flatMap(content =>
    content.type === "function_result" ? [content.callId] : [])));
  const unpersistedResults = current[proposalIndex].contents.filter((content): content is MessageContentFunctionResult =>
    content.type === "function_result" && !persistedResultCallIds.has(content.callId));

  return history.map(message => {
    if (message.id !== proposalId) return message;
    let recovered = message;
    for (const result of unpersistedResults) {
      recovered = applyApprovalContinuation(recovered, proposalId, result);
    }
    return recovered;
  });
}
